import { useState, useCallback, useMemo } from 'react';
import type { Layer, Step, StepId } from './types';

export const STEPS: Step[] = [
  { id: 'consumption', number: 1, title: 'Identifier la consommation',       subtitle: 'Prospects & gros consommateurs',      short: 'Consommation' },
  { id: 'resources',   number: 2, title: 'Ressources énergétiques',          subtitle: 'ENR & récupération de chaleur',        short: 'Ressources' },
  { id: 'routing',     number: 3, title: 'Étude de tracé du réseau',         subtitle: 'Contraintes & infrastructures',       short: 'Tracé' },
  { id: 'future',      number: 4, title: 'Développement futur',              subtitle: 'Projets & zones d\'aménagement',       short: 'Futur' },
  { id: 'context',     number: 5, title: 'Présentations & usages annexes',   subtitle: 'Contours administratifs, cadastre',   short: 'Contexte' },
];

export const CATALOG: Layer[] = [
  // 1. Consommation
  { id: 'pdl-grdf',        label: 'PDL GRDF',                       source: 'engie', steps: ['consumption'], hint: 'Points de livraison gaz' },
  { id: 'pdl-enedis',      label: 'PDL Enedis',                     source: 'engie', steps: ['consumption'] },
  { id: 'pdl-rcf',         label: 'PDL Réseaux de Chaleur / Froid', source: 'engie', steps: ['consumption'] },
  { id: 'buildings-conso', label: 'Bâtiments',                       source: 'engie', steps: ['consumption'], hint: 'Bâtiments + consommation chaud' },
  { id: 'postes-gaz',      label: 'Postes de livraison gaz',        source: 'engie', steps: ['consumption', 'routing'] },
  { id: 'postes-enedis',   label: 'Postes sources ENEDIS',          source: 'open',  steps: ['consumption', 'routing'] },
  { id: 'canal-engie',     label: 'Canalisations ENGIE existantes', source: 'engie', steps: ['consumption', 'routing'] },
  { id: 'canal-non-engie', label: 'Canalisations non ENGIE',        source: 'open',  steps: ['consumption', 'routing'] },
  // 2. Ressources
  { id: 'cheminees',       label: 'Cheminées',                      source: 'open',  steps: ['resources'], hint: 'Rejets thermiques industriels' },
  { id: 'step',            label: 'Stations d\'épuration (STEP)',   source: 'open',  steps: ['resources'] },
  { id: 'uve',             label: 'UVE — valorisation énergétique', source: 'open',  steps: ['resources'] },
  { id: 'datacenters',     label: 'Datacenters',                    source: 'open',  steps: ['resources'] },
  { id: 'entrepots-frigo', label: 'Entrepôts frigorifiques',        source: 'open',  steps: ['resources'] },
  { id: 'centrales',       label: 'Centrales ENGIE',                source: 'engie', steps: ['resources', 'consumption'] },
  { id: 'geothermie',      label: 'Géothermies sur sonde',          source: 'open',  steps: ['resources'] },
  { id: 'icpe',            label: 'ICPE',                           source: 'open',  steps: ['resources'] },
  { id: 'dry-coolers',     label: 'Dry coolers / groupes froids',   source: 'open',  steps: ['resources'] },
  // 3. Tracé
  { id: 'reseau-grdf',     label: 'Réseau GRDF',                    source: 'engie', steps: ['routing'] },
  { id: 'reseau-enedis',   label: 'Réseau ENEDIS',                  source: 'open',  steps: ['routing'] },
  { id: 'routes',          label: 'Routes',                         source: 'open',  steps: ['routing'] },
  { id: 'zones-archeo',    label: 'Zones archéologiques',           source: 'open',  steps: ['routing'] },
  { id: 'rivieres',        label: 'Rivières & hydrographie',        source: 'open',  steps: ['routing'] },
  // 4. Futur
  { id: 'projets-immo',    label: 'Projets immobiliers',            source: 'engie', steps: ['future'] },
  { id: 'zac',             label: 'ZAC — zones d\'aménagement',     source: 'open',  steps: ['future'] },
  { id: 'reseaux-const',   label: 'Réseaux en construction',        source: 'engie', steps: ['future'] },
  { id: 'zones-reseaux',   label: 'Zones de réseaux en construction', source: 'engie', steps: ['future'] },
  // 5. Présentations & usages annexes
  { id: 'contours-admin',     label: 'Contours administratifs',     source: 'open',  steps: ['context'] },
  { id: 'perimetres-contrat', label: 'Périmètre contrat (DSP)',     source: 'engie', steps: ['context'] },
  { id: 'zones-classement',   label: 'Zones de classement RCU/RFU', source: 'open',  steps: ['context'] },
  { id: 'parcelles',          label: 'Parcelles cadastrales',       source: 'open',  steps: ['context'] },
  { id: 'quartiers-prio',     label: 'Quartiers prioritaires',      source: 'open',  steps: ['context'] },
  { id: 'zones-clim',         label: 'Zones climatiques',           source: 'open',  steps: ['context'] },
  { id: 'stations-meteo',     label: 'Stations météo',              source: 'open',  steps: ['context'] },
  { id: 'servitudes',         label: 'Servitudes aéronautiques',    source: 'open',  steps: ['context'] },
];

/** Sélection suggérée par défaut (visible sur la carte au chargement). */
export const DEFAULT_LIBRARY = [
  'buildings-conso',
  'pdl-grdf',
  'cheminees',
  'centrales',
  'step',
  'uve',
  'datacenters',
  'entrepots-frigo',
  'geothermie',
  'dry-coolers',
  'icpe',
];

export function useHybridLayers() {
  const [library, setLibrary] = useState<Set<string>>(new Set(DEFAULT_LIBRARY));
  const [visible, setVisible] = useState<Set<string>>(new Set(DEFAULT_LIBRARY));
  const [openStep, setOpenStep] = useState<StepId | null>('consumption');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerStep, setPickerStep] = useState<StepId | null>(null);

  const toggleVisible = useCallback((id: string) => {
    setVisible((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);

  const toggleStep = useCallback((id: StepId) => {
    setOpenStep((p) => (p === id ? null : id));
  }, []);

  /** Bascule la présence d'une couche dans la sélection (depuis la bibliothèque). */
  const toggleLibrary = useCallback((id: string) => {
    setLibrary((p) => {
      const n = new Set(p);
      if (n.has(id)) {
        n.delete(id);
        setVisible((v) => { const nv = new Set(v); nv.delete(id); return nv; });
      } else {
        n.add(id);
        setVisible((v) => new Set(v).add(id));
      }
      return n;
    });
  }, []);

  const removeFromLibrary = useCallback((id: string) => {
    setLibrary((p) => { const n = new Set(p); n.delete(id); return n; });
    setVisible((p) => { const n = new Set(p); n.delete(id); return n; });
  }, []);

  /** Couches sélectionnées, regroupées par étape (pour le panel principal). */
  const libraryByStep = useMemo(() => {
    const map = new Map<StepId, Layer[]>();
    for (const step of STEPS) {
      map.set(step.id, CATALOG.filter((l) => library.has(l.id) && l.steps.includes(step.id)));
    }
    return map;
  }, [library]);

  /** Résultats de la bibliothèque : catalogue complet, filtré par étape + recherche. */
  const pickerResults = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    return CATALOG.filter((l) => {
      if (pickerStep && !l.steps.includes(pickerStep)) return false;
      if (q && !l.label.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [pickerSearch, pickerStep]);

  return {
    steps: STEPS,
    library,
    libraryByStep,
    visible,
    openStep,
    pickerOpen,
    pickerSearch,
    pickerStep,
    pickerResults,
    activeCount: visible.size,
    librarySize: library.size,
    catalogSize: CATALOG.length,
    setPickerOpen,
    setPickerSearch,
    setPickerStep,
    toggleVisible,
    toggleStep,
    toggleLibrary,
    removeFromLibrary,
    clearAll: () => setVisible(new Set()),
  };
}
